import lottieNewComment from "@assets/lottie/new-comment.json";
import { delay } from "@core/async/promises";
import { useI18n } from "@core/i18n/i18n";
import { libraryFilterManager } from "@core/service-container";
import { BaseExercise, Exercise, MainExercise, isMainExercise } from "@domain/exercise/exercise";
import { sendAnalyticsEventOpenAlternativeExercises } from "@domain/library/exercise-analytics";
import { useExerciseFormatter } from "@ui/components/exercise/hooks/use-exercise-formatter";
 
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { Player, PlayerEvent } from "@lottiefiles/react-lottie-player";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { ResponsiveDeviceType, useDimensions } from "@ui/hooks/use-dimensions";
import { CARD_BORDER_SIZE } from "@ui/pages/program-builder/day-table/day-exercise-placeholder";
import { ExerciseDragAndDropContext } from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import { AddAlternativesButton } from "@ui/pages/program-builder/library/components/add-alternatives-button";
import { ExerciseCardHeader } from "@ui/pages/program-builder/library/components/exercise-card-header";
import { RowExerciseCard } from "@ui/pages/program-builder/library/components/row-exercises-card";
import { DraggableCard } from "@ui/pages/program-builder/library/draggable-card";
import { ExerciseDetails } from "@ui/pages/program-builder/library/exercise-details";
import { ExerciseFooterOptions } from "@ui/pages/program-builder/library/exercise-footer-options";
import { ExerciseOptions } from "@ui/pages/program-builder/library/exercise-options";
import { OnboardingContext } from "@ui/pages/program-builder/onboarding/onboarding-context-provider";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { complementaryTooltipVisible, hideComplementaryTooltip } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { OnboardingTooltip } from "@ui/components/onboarding/onboarding-tooltip";
import { useObservable, observable } from "micro-observables";
import { colors } from "@ui/style/colors";
import { stack } from "@ui/style/layout";
import { isNotNil } from "@utils/assert";
import { HTMLMotionProps, motion } from "framer-motion";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { hideAlternativeHalo } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import { createBodyPartsFilter, createCategoriesFilter } from "@domain/exercise/exercise-filters";

type ExerciseCardStyle = "row" | "card";

interface ExerciseCardProps {
	exercise: Exercise;
	shouldAnimate?: boolean;
	draggable?: boolean;
	isDragging?: boolean;
	uiStyle?: ExerciseCardStyle;
	deviceType?: ResponsiveDeviceType.Desktop | ResponsiveDeviceType.Mobile;
	className?: string;
	showAlternatives?: boolean;
	hideContent?: boolean;
	onlyAllowPreview?: boolean;
	programEditor?: ProgramContentEditor;
	onClick?: () => void;
	onCommentIconClick?: () => void;
	Options?: JSX.Element;
	Footer?: JSX.Element;
	updateExercise?: (exercise: Exercise) => void;
	updatedExerciseId?: string;
	disabled?: boolean;
}

const COMMENT_LOTTIE_DELAY = 150; // in ms

export const DraggableExerciseCard: React.FC<
	ExerciseCardProps & {
		index: number;
		copy?: boolean;
	}
> = ({
	exercise,
	uiStyle,
	index,
	shouldAnimate,
	copy,
	showAlternatives = true,
	onlyAllowPreview,
	programEditor,
	className,
	onClick,
	onCommentIconClick,
	updatedExerciseId,
}) => (
	<DraggableCard
		exercise={exercise}
		index={index}
		renderCard={({ cardStyle, overriddenUiStyle, provided, snapshot }) => (
			<>
				<ExerciseCard
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...(provided.dragHandleProps as DragHandleProps | undefined)}
					className={className}
					style={cardStyle}
					onClick={onClick}
					onCommentIconClick={onCommentIconClick}
					shouldAnimate={shouldAnimate}
					exercise={exercise}
					programEditor={programEditor}
					showAlternatives={showAlternatives}
					onlyAllowPreview={onlyAllowPreview}
					uiStyle={overriddenUiStyle ?? uiStyle}
					id={exercise.draggableId}
					isDragging={snapshot.isDragging}
					draggable={true}
					updatedExerciseId={updatedExerciseId}
				/>
				{copy && snapshot.isDragging && <ExerciseCard exercise={exercise} uiStyle={uiStyle} />}
			</>
		)}
	/>
);

export const ExerciseCard = React.forwardRef<HTMLDivElement, ExerciseCardProps & HTMLMotionProps<"div">>(
	(
		{
			uiStyle = "card",
			deviceType = ResponsiveDeviceType.Desktop,
			exercise,
			shouldAnimate,
			draggable = false,
			isDragging = false,
			showAlternatives = true,
			hideContent = false,
			onlyAllowPreview,
			onClick,
			onCommentIconClick,
			Options,
			Footer,
			updateExercise,
			updatedExerciseId,
			disabled = false,
			...props
		},
		ref,
		// eslint-disable-next-line sonarjs/cognitive-complexity
	) => {
		// use a state instead of css to reset popup menu inside ExerciseOptions
		const dragAndDropContext = useContext(ExerciseDragAndDropContext);
		const dialogContext = useContext(DialogContext);
		useContext(OnboardingContext);
		const { isDesktop } = useDimensions();
		const { format } = useI18n();
		const { formatExerciseBodyPart, formatExerciseCategory } = useExerciseFormatter();

		const [isOver, setIsOver] = useState(false);
		const [initialComment] = useState(exercise.comment);
		const lottieRef = useRef<Player>(null);

		// Alternative onboarding removed; no per-exercise onboarding state needed

		const isComplementaryTooltipVisible = useObservable(complementaryTooltipVisible);

		const fallbackFirstExerciseId$ = useMemo(() => observable<string | undefined>(undefined), []);
		const firstExerciseDraggableId = useObservable(
			dragAndDropContext ? dragAndDropContext.programEditor.firstExerciseDraggableId : fallbackFirstExerciseId$
		);
		const showComplementaryHere =
			isComplementaryTooltipVisible && firstExerciseDraggableId === exercise.draggableId;

		const saveExercise = useCallback(
			(newExercise: Exercise) =>
				isNotNil(updateExercise)
					? updateExercise(newExercise)
					: isMainExercise(newExercise) &&
						dragAndDropContext?.programEditor.editExercise(exercise.draggableId, newExercise),
			[dragAndDropContext?.programEditor, exercise, updateExercise],
		);

		const onAlternativeExerciseSave = useCallback(
			async (newExercise: BaseExercise) => {
				saveExercise(newExercise);
				await libraryFilterManager.clearLibraryFiltersAndSearch();
			},
			[saveExercise],
		);

		const onClickAddAlternativesButton = useCallback(
			async (mainExercise: MainExercise) => {
				const filteredBodyParts = [exercise.primaryBodyPart, ...exercise.secondaryBodyParts];
				libraryFilterManager.exerciseWithAlternativesEdited.set(mainExercise);
				libraryFilterManager.activeCategoryFilters.set([
					createBodyPartsFilter(filteredBodyParts, format("exercise.bodyPart.title"), formatExerciseBodyPart),
					createCategoriesFilter([exercise.category], format("exercise.category.title"), formatExerciseCategory),
				]);
				libraryFilterManager.setIsAlternativeExerciseActive();
				isDesktop
					? dragAndDropContext?.programEditor.setExerciseWithAlternativesEdited(mainExercise)
					: dialogContext?.openModal(ModalId.MobileAlternativeExercise, {
							exercise: mainExercise,
							onSave: (e: BaseExercise) => {
								onAlternativeExerciseSave(e);
							},
						});
				sendAnalyticsEventOpenAlternativeExercises(exercise);
				hideAlternativeHalo();
			},
			[
				exercise,
				format,
				formatExerciseBodyPart,
				formatExerciseCategory,
				isDesktop,
				dragAndDropContext?.programEditor,
				dialogContext,
				onAlternativeExerciseSave,
			],
		);

		const renderExerciseOptions = useCallback(() => {
			if (isNotNil(Options)) {
				return Options;
			} else {
				return (
					<ExerciseOptionsContainerFit>
						<OnboardingTooltip
							placement="bottom"
							visible={showComplementaryHere}
							onClose={e => { hideComplementaryTooltip(); e.stopPropagation(); }}
							onClickOutside={() => hideComplementaryTooltip()}
							onHidden={() => hideComplementaryTooltip()}
							onboardingStep={OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_01}
						>
							<span style={{ display: "inline-block" }}>
								<ExerciseOptions
								exercise={exercise as MainExercise}
								onlyAllowPreview={onlyAllowPreview}
								shouldDisplayHalo={showComplementaryHere}
							/>
							</span>
						</OnboardingTooltip>
					</ExerciseOptionsContainerFit>
				);
			}
		}, [Options, showComplementaryHere, exercise, onlyAllowPreview]);

		const renderMobileFooter = useCallback(() => {
			if (deviceType !== ResponsiveDeviceType.Mobile) {
				return null;
			}
			if (isNotNil(Footer)) {
				return Footer;
			} else {
				return (
					<OnboardingTooltip
						placement="bottom"
						visible={showComplementaryHere}
						onClose={e => { hideComplementaryTooltip(); e.stopPropagation(); }}
						onClickOutside={() => hideComplementaryTooltip()}
						onHidden={() => hideComplementaryTooltip()}
						onboardingStep={OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_01}
					>
						<span style={{ display: "inline-block", width: "100%" }}>
							<ExerciseFooterOptions
							exercise={exercise as MainExercise}
							onlyAllowPreview={onlyAllowPreview}
								shouldDisplayHalo={showComplementaryHere}
							/>
						</span>
					</OnboardingTooltip>
				);
			}
		}, [Footer, deviceType, exercise, onlyAllowPreview, showComplementaryHere]);

		return (
			<ExerciseContainer
					data-test={e2eTestId.createTemplatePage.exerciseContainer}
					id={exercise.draggableId}
					ref={ref}
					{...props}
					style={{
						...props.style,
						...(draggable && { touchAction: "none" }),
					}}
					layoutId={shouldAnimate ? exercise.draggableId : undefined}
					onMouseEnter={e => {
						setIsOver(true);
						props.onMouseEnter?.(e);
					}}
					onMouseLeave={e => {
						setIsOver(false);
						props.onMouseLeave?.(e);
					}}
						onTooltipDisplayed={showComplementaryHere}
				>
					{uiStyle === "row" ? (
						<RowExerciseCard exercise={exercise} onClick={onClick} disabled={disabled} />
					) : (
						<CardExerciseContainer isOver={isOver} draggable={draggable} isDragging={isDragging}>
							{exercise.comment && (
								<ContainerCommentIcon
									onClick={event => {
										event.stopPropagation();
										onCommentIconClick?.();
									}}
								>
									<StyledPlayer
										onEvent={async event => {
											if (event === PlayerEvent.Load) {
												if (initialComment === exercise.comment) {
													lottieRef.current?.setSeeker(lottieNewComment.op - 1, false);
												} else {
													await delay(COMMENT_LOTTIE_DELAY);
													lottieRef.current?.play();
												}
											}
										}}
										src={lottieNewComment}
										ref={lottieRef}
										keepLastFrame
									/>
								</ContainerCommentIcon>
							)}
							<ExerciseCardHeader exercise={exercise} onClick={onClick} />
							{!hideContent && (
								<ExerciseCardContent>
									<ExerciseDetails
										exercise={exercise}
										withArrows={deviceType === ResponsiveDeviceType.Desktop}
										updateExercise={saveExercise}
									/>
									{deviceType === ResponsiveDeviceType.Desktop && (
										<ExerciseOptionsContainer>
											{(isOver || showComplementaryHere) &&
												renderExerciseOptions()}
										</ExerciseOptionsContainer>
									)}
								</ExerciseCardContent>
							)}
							{showAlternatives && isMainExercise(exercise) && (
								<AddAlternativesButton
									alternatives={exercise.alternativeExercises}
									onClick={() => onClickAddAlternativesButton(exercise)}
									shouldAnimateSuccess={exercise.draggableId === updatedExerciseId}
									shouldDisplayHalo={firstExerciseDraggableId === exercise.draggableId}
								/>
							)}
							{renderMobileFooter()}
						</CardExerciseContainer>
					)}
				</ExerciseContainer>
		);
	},
);
ExerciseCard.displayName = "ExerciseCard";

type DragHandleProps = DraggableProvidedDragHandleProps & { onDragStart: HTMLMotionProps<"div">["onDragStart"] };

const ExerciseContainer = styled(motion.div)<{ onTooltipDisplayed: boolean | undefined }>`
	user-select: none;
	/* Bump z-index a bit more when any onboarding tooltip is displayed to avoid card overlap */
	z-index: ${({ onTooltipDisplayed }) => (onTooltipDisplayed ? 5 : 0)};
`;

const getOutlineColor = (isOver: boolean, isDragging: boolean) => {
	if (isDragging) return colors.primary;
	if (isOver) return colors.primary25;
	return "transparent";
};

const CardExerciseContainer = styled.div<{ isOver: boolean; draggable: boolean; isDragging: boolean }>`
	${stack(12, "flexstart", "stretch")};
	flex: none;
	border-radius: 8px;
	position: relative;
	outline: ${CARD_BORDER_SIZE}px solid ${({ isOver, isDragging }) => getOutlineColor(isOver, isDragging)};
	padding-top: 24px;
	padding-bottom: 16px;
	background: ${colors.white};
	transform: ${({ isDragging }) => (isDragging ? "rotate(-2deg)" : "rotate(0deg)")};
	transition: transform 300ms ease-in-out;
	cursor: ${({ draggable }) => (draggable ? "grab" : "not-allowed")};
	touch-action: ${({ draggable }) => (draggable ? "none" : "auto")};
`;

const ContainerCommentIcon = styled.div`
	position: absolute;
	top: -10px;
	left: -10px;
	padding: 14px;
	cursor: pointer;
`;

const StyledPlayer = styled(Player)`
	width: 16px;
	height: 16px;
`;

const ExerciseCardContent = styled.div`
	display: grid;
	grid-auto-flow: column;
	grid-template-columns: 1fr auto;
	align-items: center;
	padding: 0 16px;
`;

const ExerciseOptionsContainer = styled.div`
	width: 24px;
`;

const ExerciseOptionsContainerFit = styled.div`
	width: fit-content;
`;
